import type { AuxAssign, EquipmentItem, Pump } from '@/types/dashboard';
import { AuxTypeCode, canBeTimerForWirelessButton, isSpaActuatorForWirelessButton } from '@/types/device';

export const WIRELESS_BUTTON_UNASSIGNED_SLOT = 99;
export const WIRELESS_BUTTON_SPA_MODE_SLOT = 88;

export interface WirelessBindCandidate {
  slot: number;
  label: string;
}

export const isValidWirelessUuid = (uuid?: string | null): boolean => {
  const normalized = uuid?.trim() || '';
  return normalized !== '' && normalized !== '0';
};

export const parseWirelessBindSlots = (bindButton?: string, buttonCount = 4): number[] => {
  const parts = (bindButton || '')
    .split('-')
    .map((part) => Number.parseInt(part, 10))
    .filter((value) => Number.isFinite(value));

  while (parts.length < buttonCount) {
    parts.push(WIRELESS_BUTTON_UNASSIGNED_SLOT);
  }

  return parts.slice(0, buttonCount);
};

export const getWirelessBatteryPercentage = (batteryLevel?: number): number => {
  const level = batteryLevel ?? 0;
  if (level >= 0 && level <= 100 && Number.isInteger(level)) {
    return level;
  }

  const percentage = (() => {
    if (level >= 4.1) return 100;
    if (level >= 4.0) return 90 + (level - 4.0) * 100;
    if (level >= 3.95) return 80 + (level - 3.95) * 200;
    if (level >= 3.9) return 70 + (level - 3.9) * 200;
    if (level >= 3.85) return 60 + (level - 3.85) * 200;
    if (level >= 3.8) return 50 + (level - 3.8) * 200;
    if (level >= 3.78) return 40 + (level - 3.78) * 500;
    if (level >= 3.76) return 30 + (level - 3.76) * 500;
    if (level >= 3.72) return 20 + (level - 3.72) * 250;
    if (level >= 3.6) return 10 + (level - 3.6) * 83.33;
    if (level >= 3.4) return 5 + (level - 3.4) * 25;
    if (level >= 3.0) return (level - 3.0) * 12.5;
    return 0;
  })();

  return Math.max(0, Math.min(100, Math.round(percentage)));
};

export const getWirelessBatteryLevelBucket = (percent: number): 0 | 25 | 50 | 75 | 100 => {
  if (percent === 0) return 0;
  if (percent <= 25) return 25;
  if (percent <= 50) return 50;
  if (percent <= 75) return 75;
  return 100;
};

const isTimerEnabled = (value: number | boolean | string | null | undefined): boolean => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value > 0;
  if (typeof value === 'string') {
    const normalized = value.toLowerCase();
    return normalized === 'timer' || normalized === 'true' || normalized === 'on' || (Number.parseInt(value, 10) || 0) > 0;
  }
  return false;
};

export const buildWirelessBindCandidates = (
  auxAssign?: AuxAssign,
  pumpInfo?: Pump,
): WirelessBindCandidate[] => {
  const candidates: WirelessBindCandidate[] = [];
  const timerInfo = auxAssign?.aux_timer || [];
  const hasSpaMode = { value: false };

  const pushCandidate = (slot: number, label: string) => {
    if (!candidates.some((item) => item.slot === slot)) {
      candidates.push({ slot, label });
    }
  };

  const pumpAssignedType = auxAssign?.aux_assign?.[0] ?? (pumpInfo?.pump_type ? AuxTypeCode.SPALSHME_PUMP : 0);
  if (
    pumpAssignedType === AuxTypeCode.SPALSHME_PUMP &&
    canBeTimerForWirelessButton(AuxTypeCode.SPALSHME_PUMP) &&
    isTimerEnabled(timerInfo[0])
  ) {
    pushCandidate(0, auxAssign?.aux_name?.[0] || 'Filter Pump');
  }

  (auxAssign?.equipment || []).forEach((item: EquipmentItem) => {
    if (!canBeTimerForWirelessButton(item.type_code)) return;
    if (!isTimerEnabled(timerInfo[item.slot])) return;

    if (isSpaActuatorForWirelessButton(item.type_code)) {
      if (!hasSpaMode.value) {
        hasSpaMode.value = true;
        pushCandidate(WIRELESS_BUTTON_SPA_MODE_SLOT, 'Spa Mode');
      }
      return;
    }

    pushCandidate(item.slot, item.label || 'Auxiliary');
  });

  return candidates;
};

export const getWirelessBindLabel = (
  slot: number,
  candidates: WirelessBindCandidate[],
): string => {
  if (slot === WIRELESS_BUTTON_UNASSIGNED_SLOT) return '';
  if (slot === WIRELESS_BUTTON_SPA_MODE_SLOT) return 'Spa Mode';
  return candidates.find((candidate) => candidate.slot === slot)?.label || '';
};
